import { useForm } from "@mantine/form"
import React, { useState } from "react"
import { createReportMail } from "../api"
import { notifications } from "@mantine/notifications"
import { BaseModal, requiredMark, requireLabelStyles } from "./BaseModal"
import { Box, Button, Group, ScrollArea, Stack, TextInput } from "@mantine/core"
import { confirmDiscardChanges } from "../utils/confirm"

const EMAIL_REGEXP = /^[A-Za-z0-9]{1}[A-Za-z0-9_.+-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/

interface Props {
  opened: boolean
  onClose: () => void
  clientId: number
  onSaved?: () => void
}

interface FormValues {
  name: string
  send_to: string
}

const defaultValues: FormValues = {
  name: '',
  send_to: '',
}

export const ReportMailCreateModal: React.FC<Props> = ({ opened, onClose, clientId, onSaved }) => {
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<FormValues>({
    initialValues: defaultValues,
    validate: {
      name: (v) => (!v ? '必須です' : null),
      send_to: (v) => {
        if (!v) return '必須です'
        if (!EMAIL_REGEXP.test(v)) return '正しいメールアドレスを入力してください'
        return null
      },
    },
  })

  const handleClose = () => {
    if (form.isDirty() && !confirmDiscardChanges()) {
      return
    }
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true)

    try {
      await createReportMail(clientId, values.name, values.send_to)
      notifications.show({ message: '通知先を作成しました', color: 'teal' })
      onSaved?.()
      onClose()
    } catch (errors: any) {
      const message = Array.isArray(errors) ? errors.join('\n') : '通知先の作成に失敗しました'
      notifications.show({ message, color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      title="通知先作成"
      onExited={() => {
        form.setValues(defaultValues)
        form.resetDirty()
      }}
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008" px={24}>
          <Stack gap="md" p="md">
            <TextInput
              label={<><span>名前</span>{requiredMark}</>}
              {...form.getInputProps('name')}
              styles={requireLabelStyles}
            />
            <TextInput
              label={<><span>メールアドレス</span>{requiredMark}</>}
              autoComplete="off"
              {...form.getInputProps('send_to')}
              styles={requireLabelStyles}
            />
          </Stack>
        </ScrollArea>

        <Box
          p="md"
          style={{
            borderTop: '1px solid #0000001A',
            backgroundColor: '#fff',
            flexShrink: 0,
          }}
        >
          <Group justify="flex-end" gap={8}>
            <Button
              variant="outline"
              radius="xl"
              color="gray"
              onClick={handleClose}
              disabled={submitting}
            >
              キャンセル
            </Button>
            <Button
              variant="filled"
              radius="xl"
              type="submit"
              loading={submitting}
            >
              新規作成
            </Button>
          </Group>
        </Box>
      </form>
    </BaseModal>
  )
}
